import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelfClassification, SelfDeclarationUpdateDto } from 'src/api/v2';
import { Button, Textfield } from '@innovationnorway/cl-react';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';
import { useSelfDeclaration } from 'src/features/siva/reporting/hooks/useSelfDeclaration';
import SelfClassificationSelector from './SelfClassificationSelector';
import { toIsoDate } from 'src/utils/date';

import { isCurrentSelfDeclarationValid, isValidDate } from '../../utils/self-declaration';

type Props = {
  show: boolean;
  onClose: () => void;
  sivaContractId: string;
  organizationNumber: string | undefined;
  contractNumericId?: number;
  initialSelfClassification?: SelfClassification[];
  existingDeliveredDate?: Date | string;
  acceptanceDate?: Date | string;
};

type FormProps = Omit<Props, 'show'>;

const SelfDeclarationForm: React.FC<FormProps> = ({
  onClose,
  sivaContractId,
  organizationNumber,
  contractNumericId,
  initialSelfClassification,
  existingDeliveredDate,
  acceptanceDate,
}) => {
  const { t: tCommon } = useTranslation();
  const { t: tSiva } = useTranslation('siva');
  const currentYear = new Date().getFullYear();
  const { upsertSelfDeclaration } = useSelfDeclaration(currentYear, contractNumericId);

  const hasExistingValidSelfDeclaration = isCurrentSelfDeclarationValid(existingDeliveredDate);
  const defaultDeliveredDate =
    !isValidDate(existingDeliveredDate) && isValidDate(acceptanceDate)
      ? toIsoDate(acceptanceDate)
      : toIsoDate(new Date());

  const [deliveredDate, setDeliveredDate] = useState(defaultDeliveredDate);
  const [selfClassification, setSelfClassification] = useState<Set<SelfClassification>>(
    new Set(initialSelfClassification ?? []),
  );
  const [error, setError] = useState<string | null>(null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(!hasExistingValidSelfDeclaration);

  const onSave = async () => {
    if (!deliveredDate || selfClassification.size < 1) {
      return;
    }

    try {
      setError(null);
      const update: SelfDeclarationUpdateDto = {
        selfDeclarationDelivered: true,
        selfClassification: Array.from(selfClassification),
        financialYear: currentYear,
        organizationNumber,
        sivaContractId,
        deliveredDate: new Date(deliveredDate),
      };

      await upsertSelfDeclaration.mutateAsync(update);

      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred while saving the declaration';
      setError(errorMessage);
    }
  };

  return (
    <div className="display--flex flex-direction--column gap--2">
      {!showRegistrationForm ? (
        <>
          <p>{tSiva('reporting.selfdeclaration.registerUpdatedQuestion')}</p>
          <div className="display--flex gap--1">
            <Button onClick={() => setShowRegistrationForm(true)}>
              {tSiva('reporting.selfdeclaration.registerUpdatedConfirm')}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              {tCommon('no')}
            </Button>
          </div>
        </>
      ) : (
        <>
          {error && (
            <div className="padding--1 background-color--error-light border-radius--2" role="alert">
              <p className="color--error margin--0 font-size--sm">{error}</p>
            </div>
          )}

          <Textfield
            type="date"
            label={tSiva('reporting.selfdeclaration.deliveredDate')}
            value={deliveredDate}
            onChange={(event) => setDeliveredDate(event.currentTarget.value)}
          />

          <SelfClassificationSelector
            value={selfClassification}
            onChange={setSelfClassification}
            className="margin-bottom--2"
          />

          <div className="display--flex gap--1">
            <Button
              onClick={onSave}
              disabled={!deliveredDate || selfClassification.size < 1 || upsertSelfDeclaration.isLoading}
            >
              {tCommon('save')}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              {tCommon('cancel')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const SelfDeclarationDialog: React.FC<Props> = ({ show, onClose, ...rest }) => {
  const { t: tSiva } = useTranslation('siva');
  return (
    <DialogContainer show={show} onClose={onClose} title={tSiva('reporting.selfdeclaration.dialog.title')}>
      <SelfDeclarationForm onClose={onClose} {...rest} />
    </DialogContainer>
  );
};

export default SelfDeclarationDialog;
